import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '@clerk/clerk-react';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { useSavedItems } from '../../hooks/useSavedItems';
import { MY_ORBCON_LABEL, useUserSidebar } from '../../hooks/useUserSidebar';
import { elearningAPI } from '../../services/elearningApi';
import { eventAPI } from '../../services/eventApi';
import { hubAPI } from '../../services/hubApi';
import { fetchRecentResources } from '../../services/recentResourcesApi';
import { fetchCatalogResources } from '../../services/resourceCatalogApi';
import AdminPanel from '../dashboard/AdminPanel';
import { AccessLevel } from '../../types/access';
import { Certification, Enrollment, EnrollmentStatus } from '../../types/elearning';
import { AppEvent } from '../../types/event';
import { CatalogResource } from '../../types/resourceCatalog';
import { selectFeaturedHubs, Hub } from '../../types/hub';
import { formatUserDisplayName, interestTopicLabel, readProfileExtras } from '../../types/userProfile';
import { placePath, toolkitPath } from '../../utils/placePaths';
import PlaceLink from '../layout/PlaceLink';
import HubRow from '../hubs/HubRow';
import ResourceRow from '../resources/ResourceRow';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import EventCard from '../events/EventCard';
import { CourseRow } from '../elearning/CourseCard';
import { buildResourcePath } from '../../utils/resourceLinks';
import { stripHtml } from '../../utils/htmlEntities';
import { useResourceCurrency } from '../../hooks/useResourceCurrency';
import { useRecentResources } from '../../hooks/useRecentResources';
import { programAPI } from '../../services/programApi';
import { Program } from '../../types/program';
import { clinicalAreaLabels } from '../../utils/metadataHelpers';
import { useCatalogTopicsByWpId } from '../../hooks/useCatalogTopicsByWpId';
import {
  effectiveInterestTopics,
  hubResourceWpIds,
  programResourceWpIds,
  topicListsForWpIds,
} from '../../utils/placeInterests';
import {
  NEW_FOR_YOU_CANDIDATE_PAGE,
  NEW_FOR_YOU_LIMIT,
  NEW_FOR_YOU_RECENT_SIGNAL,
  NEW_FOR_YOU_VIEW_HISTORY_LIMIT,
  buildInterestWeights,
  buildLastViewedByWpId,
  rankNewForYou,
  topWeightedTopics,
} from '../../utils/newForYou';

const LIST_LIMIT = NEW_FOR_YOU_LIMIT;
const PREVIEW_LIMIT = 3;

function eventStart(event: AppEvent): Date {
  return new Date(event.startAt);
}

function courseRecency(enrollment: Enrollment): number {
  const stamp = enrollment.lastAccessedAt || enrollment.updatedAt;
  const time = Date.parse(stamp);
  return Number.isNaN(time) ? 0 : time;
}

function Section({
  title,
  action,
  description,
  children,
}: {
  title: string;
  action?: { to: string; label: string };
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-primary pl-4">{title}</h2>
        {action && (
          <Link
            to={action.to}
            className="text-sm font-medium text-primary hover:text-primary-dark min-h-[44px] inline-flex items-center focus-ring rounded-sm"
          >
            {action.label}
          </Link>
        )}
      </div>
      {description && <div className="text-sm text-gray-600 -mt-2 mb-4">{description}</div>}
      {children}
    </section>
  );
}

function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

function ItemList({ children }: { children: ReactNode }) {
  return <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">{children}</ul>;
}

export default function PracticeHome() {
  const { user, email, accessLevel } = useAccessLevel();
  const { getToken } = useAuth();
  const { folders, ready: savedReady } = useSavedItems();
  const extras = useMemo(() => readProfileExtras(user?.unsafeMetadata), [user]);
  const topics = useMemo(() => extras.topics ?? [], [extras.topics]);
  const clinicalAreas = extras.clinicalAreas ?? [];
  const [enrollments, setEnrollments] = useState<Enrollment[] | null>(null);
  const [coursesFailed, setCoursesFailed] = useState(false);
  const [certifications, setCertifications] = useState<Certification[] | null>(null);
  const [certificatesFailed, setCertificatesFailed] = useState(false);
  const recentPosts = useRecentResources(PREVIEW_LIMIT);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [hubs, setHubs] = useState<Hub[] | null>(null);
  const [hubCatalog, setHubCatalog] = useState<Hub[]>([]);
  const [hubsFailed, setHubsFailed] = useState(false);
  const { setOpen: openMyOrbcon } = useUserSidebar();
  const [events, setEvents] = useState<AppEvent[] | null>(null);
  const [eventsFailed, setEventsFailed] = useState(false);
  const [newPosts, setNewPosts] = useState<CatalogResource[] | null>(null);
  const [postsFailed, setPostsFailed] = useState(false);
  const [newForYouUsesRecent, setNewForYouUsesRecent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    elearningAPI
      .getUserEnrollments()
      .then((rows) => {
        if (cancelled) return;
        setCoursesFailed(false);
        setEnrollments(rows);
      })
      .catch(() => {
        if (!cancelled) setCoursesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    eventAPI
      .fetchEvents()
      .then((rows) => {
        if (cancelled) return;
        const now = new Date();
        const upcoming = rows
          .map((event) => ({ event, start: eventStart(event) }))
          .filter(({ start }) => start >= now)
          .sort((a, b) => a.start.getTime() - b.start.getTime())
          .slice(0, 3)
          .map(({ event }) => event);
        setEventsFailed(false);
        setEvents(upcoming);
      })
      .catch(() => {
        if (!cancelled) setEventsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    elearningAPI
      .fetchCertifications()
      .then((rows) => {
        if (cancelled) return;
        setCertificatesFailed(false);
        setCertifications(rows);
      })
      .catch(() => {
        if (!cancelled) setCertificatesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    hubAPI
      .fetchHubs()
      .then((rows) => {
        if (cancelled) return;
        setHubsFailed(false);
        setHubCatalog(rows);
        setHubs(selectFeaturedHubs(rows));
      })
      .catch(() => {
        if (!cancelled) setHubsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    programAPI
      .fetchPrograms()
      .then((rows) => {
        if (!cancelled) setPrograms(rows);
      })
      .catch(() => {
        if (!cancelled) setPrograms([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const interestSourceWpIds = useMemo(
    () => [
      ...new Set([
        ...programs.flatMap((program) => programResourceWpIds(program)),
        ...hubCatalog.flatMap((hub) => hubResourceWpIds(hub)),
      ]),
    ],
    [programs, hubCatalog]
  );
  const { topicsByWpId } = useCatalogTopicsByWpId(interestSourceWpIds);
  const matchedHubs = useMemo(
    () =>
      hubCatalog.filter((hub) => {
        const effective = effectiveInterestTopics(
          hub.interestTopics,
          topicListsForWpIds(hubResourceWpIds(hub), topicsByWpId)
        );
        return effective.some((topic) => topics.includes(topic));
      }),
    [hubCatalog, topics, topicsByWpId]
  );
  const matchedPrograms = useMemo(
    () =>
      programs.filter((program) => {
        if (program.kind === 'REFERENCE') return false;
        const effective = effectiveInterestTopics(
          program.interestTopics,
          topicListsForWpIds(programResourceWpIds(program), topicsByWpId)
        );
        return effective.some((topic) => topics.includes(topic));
      }),
    [programs, topics, topicsByWpId]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setNewPosts(null);
      setPostsFailed(false);
      setNewForYouUsesRecent(false);
      try {
        const views = await fetchRecentResources(getToken, {
          limit: NEW_FOR_YOU_VIEW_HISTORY_LIMIT,
        }).catch(() => []);
        const recentIds = views.slice(0, NEW_FOR_YOU_RECENT_SIGNAL).map((view) => view.postId);
        let recentTopicLists: string[][] = recentIds.map(() => []);
        if (recentIds.length > 0) {
          try {
            const recentCatalog = await fetchCatalogResources(
              new URLSearchParams({
                wpIds: recentIds.join(','),
                perPage: String(recentIds.length),
              })
            );
            const byId = new Map(recentCatalog.data.map((row) => [row.wpId, row.topics ?? []]));
            recentTopicLists = recentIds.map((id) => byId.get(id) ?? []);
          } catch {
            recentTopicLists = recentIds.map(() => []);
          }
        }

        const weights = buildInterestWeights(topics, recentTopicLists);
        const queryTopics = topWeightedTopics(weights);
        if (cancelled) return;
        setNewForYouUsesRecent(recentTopicLists.some((list) => list.length > 0));
        if (queryTopics.length === 0) {
          setNewPosts([]);
          return;
        }

        const listParams = {
          sort: 'updated',
          perPage: String(NEW_FOR_YOU_CANDIDATE_PAGE),
        };
        // Profile enums map to Tools facet names; some legacy mappings still 400 upstream.
        let candidates;
        try {
          candidates = await fetchCatalogResources(
            new URLSearchParams({ ...listParams, topic: queryTopics.join(',') })
          );
        } catch {
          candidates = await fetchCatalogResources(new URLSearchParams(listParams));
        }
        if (cancelled) return;
        setNewPosts(
          rankNewForYou(candidates.data, weights, {
            lastViewedByWpId: buildLastViewedByWpId(views),
            limit: LIST_LIMIT,
          })
        );
      } catch {
        if (!cancelled) {
          setPostsFailed(true);
          setNewPosts([]);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [topics, getToken]);

  const courses = useMemo(
    () =>
      (enrollments ?? [])
        .filter(
          (enrollment) =>
            (enrollment.status === EnrollmentStatus.IN_PROGRESS ||
              enrollment.status === EnrollmentStatus.NOT_STARTED) &&
            Boolean(enrollment.course?.slug && enrollment.course.title)
        )
        .sort((a, b) => courseRecency(b) - courseRecency(a)),
    [enrollments]
  );

  const savedPostIds = useMemo(
    () =>
      folders.flatMap((folder) =>
        folder.items.flatMap((item) => (item.type === 'RESOURCE' && item.postId != null ? [item.postId] : []))
      ),
    [folders]
  );
  const { byId: savedCurrency, ready: savedCurrencyReady } = useResourceCurrency(savedPostIds);
  const saved = useMemo(
    () =>
      folders
        .flatMap((folder) => folder.items)
        .filter((item) => item.title && item.slug)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, PREVIEW_LIMIT),
    [folders]
  );
  const savedResourceCount = savedPostIds.length;
  const savedUpdates = useMemo(() => {
    const rows = folders
      .flatMap((folder) => folder.items)
      .flatMap((item) => {
        if (item.type !== 'RESOURCE' || item.postId == null || !item.slug) return [];
        const live = savedCurrency[item.postId];
        if (!live) return [];
        const modifiedMs = live.modified ? Date.parse(live.modified) : Number.NaN;
        const savedMs = Date.parse(item.createdAt);
        const changedSinceSave =
          Number.isFinite(modifiedMs) && Number.isFinite(savedMs) && modifiedMs > savedMs;
        if (!live.underRevision && !changedSinceSave) return [];
        return [
          {
            item,
            live,
            sortKey: live.underRevision ? Number.POSITIVE_INFINITY : modifiedMs,
          },
        ];
      })
      .sort((a, b) => b.sortKey - a.sortKey || b.item.createdAt.localeCompare(a.item.createdAt));
    return rows.slice(0, PREVIEW_LIMIT);
  }, [folders, savedCurrency]);

  const name = formatUserDisplayName({
    prefix: readProfileExtras(user?.unsafeMetadata).namePrefix,
    firstName: user?.firstName,
    lastName: user?.lastName,
    email,
  });
  const interestLabels = [
    ...topics.map((topic) => interestTopicLabel(topic)),
    ...clinicalAreas.map((area) => clinicalAreaLabels[area]),
  ];
  const coursesReady = !coursesFailed && enrollments !== null;
  const leadCourse = coursesReady ? courses[0] : undefined;
  const leadProgram = coursesReady && !leadCourse ? matchedPrograms[0] : undefined;
  const leadHub = coursesReady && !leadCourse && !leadProgram ? matchedHubs[0] : undefined;
  const leadRecent = coursesReady && !leadCourse && !leadProgram && !leadHub ? recentPosts[0] : undefined;
  const visibleNewPosts = newPosts ?? [];
  const leadLabel = leadCourse ? 'Continue' : leadProgram ? 'Your program' : leadHub ? 'Your toolkit' : leadRecent ? 'Recently opened' : null;
  const newestCertificate = useMemo(() => {
    if (!certifications?.length) return null;
    return [...certifications].sort((a, b) => Date.parse(b.issuedAt) - Date.parse(a.issuedAt))[0];
  }, [certifications]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
      <h1 className="text-3xl font-bold text-gray-900">
        {name === 'Account' ? 'Welcome back' : `Welcome back, ${name}`}
      </h1>

      <section className="bg-white border border-gray-200 rounded-lg p-6">
        {leadLabel && (
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{leadLabel}</p>
        )}
        {coursesFailed && <EmptyNote>Courses could not be loaded.</EmptyNote>}
        {!coursesFailed && enrollments === null && <EmptyNote>Loading…</EmptyNote>}
        {leadCourse?.course?.slug && (
          <>
            <h2 className="text-2xl font-bold text-gray-900">{leadCourse.course.title}</h2>
            {Number.isFinite(leadCourse.progress) && (
              <p className="text-sm text-gray-600 mt-1">{leadCourse.progress}% complete</p>
            )}
            {leadCourse.nextModule?.title && (
              <p className="text-sm text-gray-700 mt-1">Next: {leadCourse.nextModule.title}</p>
            )}
            <Link
              to={
                leadCourse.nextModule?.slug
                  ? `/elearning/courses/${leadCourse.course.slug}/modules/${leadCourse.nextModule.slug}`
                  : `/elearning/courses/${leadCourse.course.slug}`
              }
              className="inline-flex items-center min-h-[44px] mt-3 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            >
              Continue
            </Link>
          </>
        )}
        {leadProgram && (
          <>
            <h2 className="text-2xl font-bold text-gray-900">{leadProgram.title}</h2>
            {leadProgram.introduction && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-3">{leadProgram.introduction}</p>
            )}
            <PlaceLink
              to={placePath(leadProgram)}
              surface={{ kind: 'program', slug: leadProgram.slug }}
              className="inline-flex items-center min-h-[44px] mt-3 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            >
              Open
            </PlaceLink>
          </>
        )}
        {leadHub && (
          <>
            <h2 className="text-2xl font-bold text-gray-900">{leadHub.title}</h2>
            {leadHub.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-3">{leadHub.description}</p>
            )}
            <PlaceLink
              to={toolkitPath(leadHub)}
              surface={{ kind: 'hub', slug: leadHub.slug }}
              className="inline-flex items-center min-h-[44px] mt-3 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            >
              Open
            </PlaceLink>
          </>
        )}
        {leadRecent && (
          <>
            <h2 className="text-2xl font-bold text-gray-900">{stripHtml(leadRecent.title.rendered)}</h2>
            <PlaceLink
              to={buildResourcePath(leadRecent.slug)}
              surface={{ kind: 'resource', slug: leadRecent.slug }}
              className="inline-flex items-center min-h-[44px] mt-3 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            >
              Open
            </PlaceLink>
          </>
        )}
        {coursesReady && !leadCourse && !leadProgram && !leadHub && !leadRecent && (
          <EmptyNote>
            Nothing is waiting.{' '}
            <Link to="/elearning" className="text-primary hover:text-primary-dark font-medium focus-ring rounded-sm">
              Browse courses
            </Link>
            {interestLabels.length === 0 && (
              <>
                {' '}
                or add topics on your{' '}
                <Link to="/profile" className="text-primary hover:text-primary-dark font-medium focus-ring rounded-sm">
                  profile
                </Link>
              </>
            )}
            .
          </EmptyNote>
        )}
      </section>

      {certificatesFailed && <EmptyNote>Certificates could not be loaded.</EmptyNote>}
      {newestCertificate && certifications && (
        <p className="text-sm text-gray-700">
          {certifications.length === 1 ? '1 certificate' : `${certifications.length} certificates`}. Newest:{' '}
          <Link
            to="/elearning/certifications"
            className="font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
          >
            {newestCertificate.course?.title || 'Completed course'}
          </Link>
          <span className="text-gray-500">, {format(new Date(newestCertificate.issuedAt), 'MMM d, yyyy')}</span>
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="order-2 lg:order-1 lg:col-span-2 space-y-10">
      {courses.length > 1 && (
      <Section title="Also in progress" action={{ to: '/elearning', label: 'All courses' }}>
        <div className="space-y-3">
          {courses.slice(1, LIST_LIMIT).map((enrollment) =>
            enrollment.course ? (
              <CourseRow key={enrollment.id} course={enrollment.course} enrollment={enrollment} />
            ) : null
          )}
        </div>
      </Section>
      )}

      <Section title="ORBCoN calendar" action={{ to: '/events', label: 'All events' }}>
        {eventsFailed && <EmptyNote>Events could not be loaded.</EmptyNote>}
        {!eventsFailed && events === null && <EmptyNote>Loading events…</EmptyNote>}
        {!eventsFailed && events !== null && events.length === 0 && (
          <EmptyNote>No upcoming events right now.</EmptyNote>
        )}
        {events && events.length > 0 && (
          <div className="space-y-3">
            {events.map((event) => (
              <EventCard key={event.id} event={event} layout="row" />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="New for you"
        description={
          visibleNewPosts.length > 0
            ? newForYouUsesRecent
              ? 'Based on your interests and recent viewing.'
              : 'Based on your interests.'
            : undefined
        }
        action={{ to: '/library', label: 'Library' }}
      >
        {postsFailed && <EmptyNote>New resources could not be loaded.</EmptyNote>}
        {!postsFailed && newPosts === null && <EmptyNote>Loading resources…</EmptyNote>}
        {!postsFailed && newPosts !== null && newPosts.length === 0 && topics.length === 0 && (
          <EmptyNote>
            Add topics on your{' '}
            <Link to="/profile" className="text-primary hover:text-primary-dark font-medium focus-ring rounded-sm">
              profile
            </Link>{' '}
            or open a few resources so we can match documents for you.
          </EmptyNote>
        )}
        {!postsFailed && newPosts !== null && newPosts.length === 0 && topics.length > 0 && (
          <EmptyNote>No strong matches for your interests right now.</EmptyNote>
        )}
        {visibleNewPosts.length > 0 && (
          <div className="space-y-3">
            {visibleNewPosts.map((post) => (
              <ResourceRow
                key={post.wpId}
                id={post.wpId}
                title={stripHtml(post.title)}
                slug={post.slug}
                date={post.wpModifiedAt ?? post.wpCreatedAt}
                underRevision={post.underRevision === true}
              />
            ))}
          </div>
        )}
      </Section>

      <Section title="From ORBCoN" action={{ to: '/resources', label: 'Programs' }}>
        {hubsFailed && <EmptyNote>Hubs could not be loaded.</EmptyNote>}
        {!hubsFailed && hubs === null && <EmptyNote>Loading hubs…</EmptyNote>}
        {!hubsFailed && hubs !== null && hubs.length === 0 && (
          <EmptyNote>No featured toolkits yet.</EmptyNote>
        )}
        {hubs && hubs.length > 0 && (
          <div className="space-y-3">
            {hubs.map((hub) => (
              <HubRow key={hub.id} hub={hub} />
            ))}
          </div>
        )}
      </Section>
        </div>

        <div className="order-1 space-y-10 lg:order-2">
          <Section title="Saved">
            {!savedReady && <EmptyNote>Loading saved items…</EmptyNote>}
            {savedReady && saved.length === 0 && (
              <EmptyNote>Resources and toolkits you save will appear here.</EmptyNote>
            )}
            {saved.length > 0 && (
              <div className="space-y-3">
                {saved.map((item) => {
                  if (item.type === 'HUB' && item.hubId) {
                    const knownHub = hubCatalog.find((hub) => hub.id === item.hubId || hub.slug === item.slug);
                    return (
                      <HubRow
                        key={item.id}
                        hub={{
                          id: item.hubId,
                          title: stripHtml(item.title),
                          slug: item.slug,
                          resources: knownHub?.resources ?? [],
                          program: knownHub?.program,
                        }}
                      />
                    );
                  }
                  if (item.type === 'RESOURCE' && item.postId) {
                    const live = savedCurrency[item.postId];
                    return (
                      <ResourceRow
                        key={item.id}
                        id={item.postId}
                        title={stripHtml(live?.title || item.title)}
                        slug={item.slug}
                        underRevision={live?.underRevision}
                        date={live && !live.underRevision ? live.modified : undefined}
                        datePrefix={live && !live.underRevision ? 'Updated' : undefined}
                      />
                    );
                  }
                  if (item.type !== 'PROGRAM') return null;
                  const knownPlace = programs.find((program) => program.slug === item.slug);
                  const programTitle = stripHtml(item.title);
                  const programTo = knownPlace ? placePath(knownPlace) : `/${item.slug}`;
                  const kindLabel = knownPlace?.kind === 'REFERENCE' ? 'Reference' : 'Program';
                  return (
                    <Card key={item.id} className="!p-3">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-600">{kindLabel}</p>
                          <h3 className="mt-0.5 text-base font-semibold text-gray-900">{programTitle}</h3>
                        </div>
                        {knownPlace && (
                          <SaveToMyOrbconButton
                            type="PROGRAM"
                            programId={knownPlace.id}
                            title={programTitle}
                            slug={item.slug}
                            variant="icon"
                            align="end"
                          />
                        )}
                      </div>
                      <div className="mt-2">
                        <span className="inline-flex min-h-[44px] items-center">
                          <QuietAction to={programTo} label={`Open ${programTitle}`}>
                            Open
                          </QuietAction>
                        </span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={() => openMyOrbcon(true)}
              className="inline-flex items-center min-h-[44px] mt-3 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            >
              Open {MY_ORBCON_LABEL}
            </button>
          </Section>

          <Section title="Updates">
            {(!savedReady || (savedResourceCount > 0 && !savedCurrencyReady)) && (
              <EmptyNote>Loading updates…</EmptyNote>
            )}
            {savedReady && savedCurrencyReady && savedResourceCount === 0 && (
              <EmptyNote>Save a resource to see when it changes.</EmptyNote>
            )}
            {savedReady && savedCurrencyReady && savedResourceCount > 0 && savedUpdates.length === 0 && (
              <EmptyNote>No recent updates to resources you've saved.</EmptyNote>
            )}
            {savedUpdates.length > 0 && (
              <div className="space-y-3">
                {savedUpdates.map(({ item, live }) => (
                  <ResourceRow
                    key={item.id}
                    id={item.postId!}
                    title={stripHtml(live.title || item.title)}
                    slug={item.slug}
                    underRevision={live.underRevision}
                    date={!live.underRevision ? live.modified ?? undefined : undefined}
                    datePrefix={!live.underRevision ? 'Updated' : undefined}
                  />
                ))}
              </div>
            )}
          </Section>

          <Section title="Recently viewed">
            {recentPosts.length === 0 ? (
              <EmptyNote>Resources you open will appear here.</EmptyNote>
            ) : (
              <div className="space-y-3">
                {recentPosts.map((post) => (
                  <ResourceRow key={post.id} post={post} />
                ))}
              </div>
            )}
          </Section>

          <Section title="Your events">
            <ItemList>
              <li className="px-4 py-3">
                <p className="text-sm text-gray-700">Events you save or register for will appear here.</p>
                <p className="text-xs text-gray-500 mt-1">Not connected yet.</p>
              </li>
            </ItemList>
          </Section>
        </div>
      </div>

      {accessLevel === AccessLevel.Admin && <AdminPanel />}
    </div>
  );
}
